import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import ClipCard from "../ClipCard/ClipCard";
import { axiosInstance } from "../../config";
import "./ClipsModal.css";

// Deterministic hook score per clip (60–99), based on clip id + text + duration
function computeHookScore(clip, index) {
  const idSeed = clip._id
    ? [...clip._id].reduce((acc, c) => acc + c.charCodeAt(0), 0)
    : (index + 1) * 73;
  const base = 62 + (idSeed % 22);
  const hookLen = clip.hook?.length || 0;
  const hookBonus = hookLen >= 15 && hookLen <= 75 ? Math.min(10, Math.floor(hookLen / 7)) : 0;
  const dur = clip.duration || 0;
  const durBonus = dur >= 25 && dur <= 55 ? 5 : 0;
  return Math.min(99, base + hookBonus + durBonus);
}

function Confetti() {
  const canvasRef = useRef(null);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const COLORS = ['#ff6a00', '#ffaa00', '#ff8533', '#ffffff', '#ffcc00'];
    const pieces = Array.from({ length: 55 }, () => ({
      x: Math.random() * canvas.width,
      y: -20 - Math.random() * 80,
      w: 6 + Math.random() * 7,
      h: 4 + Math.random() * 4,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      vx: (Math.random() - 0.5) * 3,
      vy: 2.5 + Math.random() * 3,
      angle: Math.random() * Math.PI * 2,
      spin: (Math.random() - 0.5) * 0.15,
      opacity: 1,
    }));
    let frame;
    let tick = 0;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      tick++;
      let anyAlive = false;
      for (const p of pieces) {
        p.x += p.vx;
        p.y += p.vy;
        p.angle += p.spin;
        if (tick > 45) p.opacity -= 0.018;
        if (p.y < canvas.height + 30 && p.opacity > 0) {
          anyAlive = true;
          ctx.save();
          ctx.globalAlpha = Math.max(0, p.opacity);
          ctx.translate(p.x, p.y);
          ctx.rotate(p.angle);
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
          ctx.restore();
        }
      }
      if (anyAlive) { frame = requestAnimationFrame(animate); }
      else { setVisible(false); }
    };
    animate();
    return () => cancelAnimationFrame(frame);
  }, []);

  if (!visible) return null;
  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 99999 }}
    />
  );
}

const ClipsModal = ({ job, videoTitle, onClose, userPlan, onUpgrade, onGenerateAnother }) => {
  const { t } = useTranslation('clips');
  const [votes, setVotes] = useState({});
  const [nextUrl, setNextUrl] = useState("");
  const [showConfetti] = useState(true);

  // Fetch existing votes when modal opens
  useEffect(() => {
    if (!job?._id) return;
    const fetchVotes = async () => {
      try {
        const res = await axiosInstance.get(`/clips/feedback/${job._id}`);
        setVotes(res.data.votes || {});
      } catch (err) {
        console.log("Could not fetch feedback:", err);
      }
    };
    fetchVotes();
  }, [job?._id]);

  if (!job || !job.clips?.length) return null;

  // Compute hook scores for all clips
  const hookScores = job.clips.map((clip, i) => computeHookScore(clip, i));
  const bestScore = Math.max(...hookScores);
  const bestIndex = hookScores.indexOf(bestScore);

  // Get vote for a clip by its subdoc _id
  const getVoteForClip = (clip) => {
    if (!clip._id) return null;
    return votes[clip._id] || null;
  };

  const handleVote = (index, newVote) => {
    const clip = job.clips[index];
    if (!clip?._id) return;
    setVotes((prev) => {
      const updated = { ...prev };
      if (newVote) {
        updated[clip._id] = newVote;
      } else {
        delete updated[clip._id];
      }
      return updated;
    });
  };

  return (
    <>
      {showConfetti && <Confetti />}
      <div className="clips-modal-overlay" onClick={onClose}>
        <div className="clips-modal" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="clips-modal-header">
            <div className="clips-modal-header-content">
              <h3 className="clips-modal-title">
                <span className="clips-modal-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="23 7 16 12 23 17 23 7" />
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                  </svg>
                </span>
                {t('yourClips')}
              </h3>
              {videoTitle && (
                <p className="clips-modal-subtitle">{videoTitle}</p>
              )}
            </div>
            <button className="clips-modal-close" onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Clips count */}
          <div className="clips-modal-count">
            <span className="clips-modal-count-badge">{t('clipCount', { count: job.clips.length })}</span>
          </div>

          {/* Clips grid */}
          <div className="clips-modal-grid">
            {job.clips.map((clip, index) => (
              <div
                key={index}
                className="clips-modal-card-wrapper"
                style={{ animationDelay: `${index * 0.12}s` }}
              >
                <ClipCard
                  clip={clip}
                  index={index}
                  clipJobId={job._id}
                  userPlan={userPlan}
                  onUpgrade={onUpgrade}
                  vote={getVoteForClip(clip)}
                  onVote={handleVote}
                  hookScore={hookScores[index]}
                  isBestClip={index === bestIndex && job.clips.length > 1}
                />
              </div>
            ))}
          </div>

          {/* Generate Another */}
          {onGenerateAnother && (
            <div className="clips-modal-generate-another">
              <p className="clips-modal-generate-label">Generate from another video</p>
              <div className="clips-modal-generate-row">
                <input
                  type="text"
                  value={nextUrl}
                  onChange={(e) => setNextUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && nextUrl.trim()) onGenerateAnother(nextUrl.trim());
                  }}
                  placeholder="Paste a video URL..."
                  className="clips-modal-generate-input"
                />
                <button
                  onClick={() => { if (nextUrl.trim()) onGenerateAnother(nextUrl.trim()); }}
                  disabled={!nextUrl.trim()}
                  className="clips-modal-generate-btn"
                >
                  Generate
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ClipsModal;
